<template>
  <teleport to="body">
    <transition name="fade">
      <div
        class="fixed top-0 left-0 px-15 pt-70 w-full h-full bg-backdrop z-9999 md:z-100"
        @click.self="close"
      >
        <div
          id="wrapper"
          class="max-h-full relative overflow-y-auto pt-50 md:pt-75 md:pb-100 px-15 md:px-100 bg-primary mx-auto max-w-1020 w-full rounded-t-4"
          :class="{'rounded-b-4': showBorderRadius}"
        >
          <div>
            <div class="text-app-color">
              <input
                id="title"
                ref="titleInputRef"
                v-model="localEvent.title"
                placeholder="What’s the event?"
                class="w-full mb-10 text-30 bg-transparent outline-none font-bold px-5 leading-small block"
                maxlength="40"
                type="text"
                @focus="blockHotKey(true)"
                @blur="blockHotKey(false)"
                @input="saveTitle"
              >

              <textarea
                id="title"
                ref="titleInputRef"
                v-model="localEvent.subTitle"
                rows="3"
                placeholder="Describe it in more detail..."
                class="w-full mb-10 text-15 bg-transparent outline-none px-5 block"
                @focus="blockHotKey(true)"
                @blur="blockHotKey(false)"
                @input="saveSubTitle"
              />

              <div class="md:flex">
                <div
                  v-if="attachments || countMessages > 0 || isTeamAccount"
                  class="flex items-center border-r border-grey-800 pr-10 md:mr-15 -mx-5"
                  :class="isDarkMode ? 'text-app-color' : 'text-dark-500'"
                >
                  <div v-if="attachments" class="flex items-center opacity-30 mx-5">
                    <i class="icon-attach-file text-22" />
                    <p>{{ attachments }}</p>
                  </div>

                  <div v-if="countMessages > 0" class="flex items-center opacity-30 mx-5">
                    <i class="icon-chat mr-3" />
                    <p class="leading-none">{{ countMessages }}</p>
                  </div>

                  <div v-if="isTeamAccount && members" class="flex items-center opacity-30 mx-5">
                    <i class="icon-supervisor-account text-22 mr-3" />
                    <p>{{ members }}</p>
                  </div>
                </div>

                <div class="flex items-center mt-10 md:mt-0">
                  <TagsModal
                    tag-class="h-25 text-13 leading-tight"
                    :selected-tags="event.event.eventTags"
                    :tags="tags"
                    @toggleTag="toggleEventTag"
                    @updateTag="updateTag"
                  />

                  <Mentions v-model="project" class="ml-10" />
                </div>
              </div>
            </div>

            <DepthPageEditor
              class="mt-50"
              :event-id="event.event.id"
              :book-id="event.event.bookId"
              :additional-info="event.event.additionalInfo"
              @updateContent="updateContent"
              @blockHotKey="blockHotKey"
              @save="saveAdditionalInfo"
            />

            <DepthPageChat
              class="mt-20"
              :allow-hot-key="allowHotKey"
              :event-id="event.event.id"
              :book-id="event.event.bookId"
              :chat-id="event.event.chat.id"
              :mentions="projects"
              :messages="messages"
            />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
/* eslint-disable */
import { defineComponent, computed, onMounted, ref } from 'vue'

import { useStore } from 'vuex'
import TagsModal from '@/components/week-planner/TagsModal'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Mentions from '@/components/week-planner/Mentions'
import DepthPageEditor from '@/components/shared/depth-page/DepthPageEditor'
import DepthPageChat from '@/components/shared/depth-page/DepthPageChat'

export default defineComponent({
  name: 'EventModal',

  components: {
    TagsModal,
    ButtonModule,
    Mentions,
    DepthPageEditor,
    DepthPageChat
  },

  props: {
    event: Object,
  },

  emits: ['close', 'update:modelValue', 'updateTag', 'getUpcoming'],

  setup (props, { emit }) {
    const { state, commit, dispatch, getters } = useStore()

    const user = computed(() => state.profile.user)

    const isDarkMode = computed(() => getters.isDarkMode)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const books = computed(() => state.book.books.filter(b => !b.isSection))
    const tags = computed(() => state.planner.tags.filter(tag => tag.bookId === selectedBook.value?.id))
    const selectedBook = computed(() => books.value.find(b => b.id === props.event.event.bookId))
    const showBorderRadius = ref(true)

    const projects = computed(() => state.project.projects)

    const countMessages = computed(() => {
      const msgs = state.chat.depthPageMessages || []
      return msgs.length > 50 ? 50 : msgs.length
    })

    const messages = computed(() => [])

    const attachments = ref(0)

    const members = computed(() => selectedBook.value?.shareWith?.length || state.team.members.length)

    const allowHotKey = ref(true)

    onMounted(async () => {
      await dispatch('getDepthPageMessages', {
        bookId: props.event.event.bookId,
        chatId: props.event.event.chat.id
      })

      const wrapper = document.getElementById('wrapper')

      function onResize () {
        const paddingTop = 70
        const height = wrapper.offsetHeight
        const maxHeight = window.innerHeight - paddingTop
        showBorderRadius.value = height < maxHeight
      }
      onResize()

      new ResizeObserver(onResize).observe(wrapper)
    })

    const project = computed({
      get: () => props.event.event.project,
      set: (value) => {
        dispatch('updateEvent', {
          id: props.event.id,
          bookId: props.event.event.bookId,
          projectId: value?.id || null
        })

        commit('updateEvent', {
          id: props.event.id,
          event: {
            ...props.event.event,
              bookId: props.event.event.bookId,
              project: value || null,
              projectId: value?.id || null
          }
        })
      }
    })

    const localEvent = computed({
      get: () => {
        return props.event.event
      },
      set: (value) => {
        commit('updateEvent', {
          id: props.event.id,
          event: value
        })
      }
    })

    const files = computed(() => state.file.files)

    function close () {
      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === props.event.event.bookId)) {
        commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
        return false
      } else {
        emit('close')
      }
    }

    function toggleEventTag ({ tag, isSelected }) {
      dispatch('toggleEventTag', {
        tag: tag,
        eventId: props.event.event.id,
        isSelected: isSelected,
        bookId: props.event.event.bookId
      })
    }


    function updateTag ({ name, id }) {
      emit('updateTag', { name, id })
    }

    let timer = null

    function saveTitle (e) {
      clearTimeout(timer)
      if (localEvent.value.title.trim() === '') {
        localEvent.value.title = props.event.event.title
        return
      }

      timer = setTimeout(() => {
        save('title')
      }, 300)
    }

    function saveSubTitle (e) {
      clearTimeout(timer)

      timer = setTimeout(() => {
        save('subTitle')
      }, 300)
    }

    function save (prop) {
      const data = {}

      data[prop] = localEvent.value[prop]
      dispatch('updateEvent', {
          id: props.event.id,
          ...data,
            bookId: props.event.event.bookId
        }).then(() => emit('getUpcoming'))

        commit('updateEvent', {
          id: props.event.id,
          event: {
            ...props.event.event,
            ...data,
              bookId: props.event.event.bookId,
          }
        })
    }

    function blockHotKey (value) {
      allowHotKey.value = !value
    }

    function updateContent (value) {
      attachments.value = value.attachments
      emit('update:modelValue', value)
    }

    function saveAdditionalInfo (value) {
      dispatch('updateEvent', {
        id: props.event.id,
        bookId: props.event.event.bookId,
        additionalInfo: value
      })

      commit('updateEvent', {
        id: props.event.id,
        event: {
          ...props.event.event,
        additionalInfo: value,
          bookId: props.event.event.bookId,
        }
      })
    }

    return {
      // refs
      // data
      localEvent,
      allowHotKey,
      // computed
      tags,
      user,
      project,
      members,
      projects,
      messages,
      isDarkMode,
      attachments,
      isTeamAccount,
      countMessages,
      showBorderRadius,
      // function
      close,
      updateTag,
      saveTitle,
      blockHotKey,
      saveSubTitle,
      updateContent,
      toggleEventTag,
      saveAdditionalInfo
    }
  }
})
</script>
